import { useEffect, useState } from "react";
import { Checkbox } from "./ui/checkbox";
import { Sel } from "./Fields";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";

const NumPair = ({ label, keys, params, update, testid }) => {
  const { t } = useI18n();
  const pa = params.get(keys[0]) || "";
  const pb = params.get(keys[1]) || "";
  const [v, setV] = useState({ a: pa, b: pb });
  useEffect(() => setV({ a: pa, b: pb }), [pa, pb]);
  const commit = () => update({ [keys[0]]: v.a, [keys[1]]: v.b });
  const onKey = (e) => e.key === "Enter" && (e.preventDefault(), commit());
  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wider text-fg/60">{label}</p>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="0" inputMode="numeric" aria-label={`${label} ${t("listing.min")}`} placeholder={t("listing.min")} data-testid={`${testid}-min`} className="field !py-2.5" dir="ltr" value={v.a} onChange={(e) => setV((s) => ({ ...s, a: e.target.value }))} onBlur={commit} onKeyDown={onKey} />
        <input type="number" min="0" inputMode="numeric" aria-label={`${label} ${t("listing.max")}`} placeholder={t("listing.max")} data-testid={`${testid}-max`} className="field !py-2.5" dir="ltr" value={v.b} onChange={(e) => setV((s) => ({ ...s, b: e.target.value }))} onBlur={commit} onKeyDown={onKey} />
      </div>
    </div>
  );
};

const Steps = ({ label, k, params, update, max = 5 }) => {
  const { t } = useI18n();
  const cur = params.get(k) || "";
  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wider text-fg/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {["", ...Array.from({ length: max }, (_, i) => String(i + 1))].map((n) => (
          <button key={n || "any"} type="button" data-testid={`filter-${k}-${n || "any"}`} aria-pressed={cur === n} onClick={() => update({ [k]: n })} className={`chip !px-3 !py-1.5 ${cur === n ? "chip-on" : ""}`}>{n ? `${n}+` : t("search.any")}</button>
        ))}
      </div>
    </div>
  );
};

export default function FilterPanel({ params, update, clear, preset = {} }) {
  const { t, tr } = useI18n();
  const { locations, byKind, city } = useSite();
  const g = (k) => params.get(k) || "";
  const selCity = city(g("city"));
  const amen = g("amenities") ? g("amenities").split(",") : [];
  const toggleAmen = (k) => update({ amenities: (amen.includes(k) ? amen.filter((x) => x !== k) : [...amen, k]).join(",") });
  const types = byKind("property_type").filter((c) => !preset.category || (preset.category === "land" ? c.key === "land" : preset.category === "commercial" ? ["office", "retail", "commercial_building"].includes(c.key) : ["apartment", "villa", "townhouse"].includes(c.key)));
  return (
    <div data-testid="filter-panel" className="space-y-6">
      <Sel testid="filter-city" label={t("search.city")} value={g("city")} onChange={(v) => update({ city: v, neighborhood: "" })} placeholder={t("search.anyCity")} options={locations.map((l) => [l.slug, tr(l, "name")])} />
      {selCity && <Sel testid="filter-neighborhood" label={t("listing.neighborhood")} value={g("neighborhood")} onChange={(v) => update({ neighborhood: v })} placeholder={t("listing.anyNeighborhood")} options={selCity.neighborhoods.map((n) => [n.slug, tr(n, "name")])} />}
      <Sel testid="filter-type" label={t("search.type")} value={g("type")} onChange={(v) => update({ type: v })} placeholder={t("search.anyType")} options={types.map((c) => [c.key, tr(c, "label")])} />
      {!preset.purpose && <Sel testid="filter-purpose" label={t("search.purpose")} value={g("purpose")} onChange={(v) => update({ purpose: v })} placeholder={t("search.any")} options={["sale", "rent", "investment"].map((p) => [p, t(`purpose.${p}`)])} />}
      <NumPair label={t("listing.price")} keys={["min_price", "max_price"]} params={params} update={update} testid="filter-price" />
      {preset.category !== "land" && <Steps label={t("listing.bedrooms")} k="bedrooms" params={params} update={update} />}
      {preset.category !== "land" && <Steps label={t("listing.bathrooms")} k="bathrooms" params={params} update={update} />}
      <NumPair label={t("listing.size")} keys={["min_size", "max_size"]} params={params} update={update} testid="filter-size" />
      <Sel testid="filter-completion" label={t("listing.completion")} value={g("completion")} onChange={(v) => update({ completion: v })} placeholder={t("search.any")} options={[["ready", t("listing.ready")], ["off_plan", t("listing.off_plan")]]} />
      <Steps label={t("listing.parking")} k="parking" params={params} update={update} max={3} />
      <Sel testid="filter-furnishing" label={t("listing.furnishing")} value={g("furnishing")} onChange={(v) => update({ furnishing: v })} placeholder={t("search.any")} options={["furnished", "semi_furnished", "unfurnished"].map((k) => [k, t(`listing.${k}`)])} />
      <Sel testid="filter-availability" label={t("listing.availability")} value={g("availability")} onChange={(v) => update({ availability: v })} placeholder={t("search.any")} options={["available", "reserved", "sold", "rented"].map((k) => [k, t(`listing.${k}`)])} />
      <fieldset>
        <legend className="mb-3 text-xs uppercase tracking-wider text-fg/60">{t("listing.amenities")}</legend>
        <div className="grid grid-cols-1 gap-2.5">
          {byKind("amenity").map((a) => (
            <label key={a.key} className="flex cursor-pointer items-center gap-3 text-sm text-fg/75 hover:text-fg">
              <Checkbox data-testid={`filter-amenity-${a.key}`} checked={amen.includes(a.key)} onCheckedChange={() => toggleAmen(a.key)} className="border-cta/50 data-[state=checked]:bg-cta data-[state=checked]:text-canvas" />
              {tr(a, "label")}
            </label>
          ))}
        </div>
      </fieldset>
      <button type="button" data-testid="filter-clear-button" onClick={clear} className="btn-ghost w-full">{t("listing.clear")}</button>
    </div>
  );
}
